import { useLocation } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import {
    Sidebar as AppSidebar,
    SidebarAccount,
    SidebarList,
    SidebarListItem,
    SidebarNav,
    useAuthentication,
    useCurrentUser,
} from '@freelancelabs/components';
import {
    BellOutline,
    ChartBarOutline,
    FolderOutline,
    HouseOutline,
    LayersOutline,
    LogoutOutline,
    MemoOutline,
    PageOutline,
    PeopleOutline,
    SettingsOutline,
    UserCircleOutline,
} from '@freelancelabs/icons';

import { AppRoutes, DataIntercomTarget } from '../../constants';
import useViewerAttachment from '../../domains/shared/hooks/useViewerAttachment';
import { MyModulesDocument, ViewerDocument } from '../../graphql/__generated__/types';

/**
 * Le module qu'une entrée sert, ou `null` pour celles qui n'en dépendent pas.
 *
 * **Attention, le code, jamais le libellé.** `PortalModule.code` est l'identifiant stable
 * que le front associe à une route ; le libellé vient du référentiel et change.
 *
 * « Accueil » n'a pas de module et ne peut pas en avoir : c'est la page où l'on
 * arrive, y compris quand rien n'est ouvert.
 */
const MODULE_OF: { [route: string]: string | null } = {
    [AppRoutes.HOME]: null,
    [AppRoutes.DASHBOARD]: 'dashboard',
    [AppRoutes.NEEDS]: 'needs',
    [AppRoutes.SERVICES]: 'activity',
    [AppRoutes.PARTNERS]: 'partners',
    [AppRoutes.PURCHASE_ORDERS]: 'orders',
    [AppRoutes.INVOICES]: 'invoices',
};

const Sidebar = () => {
    const { logout } = useAuthentication();
    const { pathname } = useLocation();
    /**
     * L'IDENTITE VIENT DE L'API ET NON DU JETON, et les deux ne disent pas la même
     * chose.
     *
     * `useCurrentUser` lit `given_name` et `family_name` dans l'idToken Cognito :
     * ce sont les valeurs que le pool a reçues à la création du compte, et rien ne
     * les tient à jour. `viewer` lit notre propre table `user`, que l'onboarding
     * remplit depuis le contact ingéré et qui suit la source. Quand le pool ne porte
     * pas ces claims, le nom était vide et la barre retombait sur l'ADRESSE : c'est
     * ce que ce lot corrige.
     *
     * Aucune requête de plus : `Viewer` est déjà lue par l'accueil et par
     * `useViewerAttachment` juste en dessous, donc Apollo la sert de son cache.
     *
     * Le jeton reste le repli, et c'est délibéré : il est disponible AVANT la
     * réponse de l'API, donc la barre affiche un nom au premier rendu au lieu de
     * clignoter. Deux sources pour un fait est un défaut quand les deux prétendent
     * dire vrai ; ici l'une des deux est explicitement le repli de l'autre.
     */
    const { email: emailDuJeton, firstName: prenomDuJeton, lastName: nomDuJeton } = useCurrentUser();
    const { data: viewerData } = useQuery(ViewerDocument);
    const viewer = viewerData?.viewer;

    const firstName = viewer?.firstName ?? prenomDuJeton;
    const lastName = viewer?.lastName ?? nomDuJeton;
    const email = viewer?.email ?? emailDuJeton;
    /*
     * L'adresse reste le dernier repli, mais elle n'est plus le cas ordinaire : une
     * personne sans prénom ni nom NULLE PART existe (un contact ingéré peut n'en
     * porter aucun), et lui écrire « Mon compte » à la place de son adresse lui
     * retirerait le seul repère qui l'identifie.
     */
    const displayName = [firstName, lastName].filter(Boolean).join(' ') || email || c('Sidebar').t`My account`;

    /**
     * **Le menu suit les modules ouverts sur les comptes de la personne** (PCG-104).
     *
     * Il portait ses sept entrées en dur et n'interrogeait rien. La conséquence
     * n'était pas théorique : le module « Tableau de bord » n'est ouvert sur presque
     * aucun compte, donc le portail invitait le lecteur à une page qui le refusait
     * ensuite : par une entrée qu'il lui avait lui-même proposée.
     *
     * **Attention, ce menu n'est pas une sécurité, et il ne faut pas le lire comme tel.**
     * Masquer une entrée ne ferme pas son URL ; ce qui ferme est
     * `MODULE_NOT_ENABLED`, rendu par chaque chemin de lecture, et chaque page doit
     * continuer à l'écrire. Ce qu'on gagne ici est de ne pas PROMETTRE une page qui
     * refusera.
     *
     * Attention, `myModules` est une **union** sur les rattachements, jamais une
     * intersection : un module ouvert sur un seul compte ouvre l'entrée, et les
     * autres comptes refusent la lecture. L'entrée du menu ne dit donc rien de ce
     * qu'on verra dedans, et c'est voulu : l'inverse ferait disparaître un module
     * quand on AJOUTE un rattachement.
     */
    const { data, loading, error } = useQuery(MyModulesDocument);
    const openModules = new Set((data?.myModules ?? []).map((module) => module.code));

    /*
     * Lu ici pour une seule raison : savoir se taire. Le menu ne change pas d'un iota
     * selon les rattachements : c'est `myModules` qui le gouverne, et il rend déjà une
     * liste vide dans ce cas. Voir la note plus bas.
     */
    const attachment = useViewerAttachment();

    // The seven entries of the V2 mockup, in its order. Icons are the mockup's own
    // symbols, redrawn in @freelancelabs/icons.
    const entries = [
        {
            title: c('Sidebar').t`Home`,
            icon: <HouseOutline />,
            to: AppRoutes.HOME,
            active: pathname === AppRoutes.HOME,
            target: DataIntercomTarget.CTA_HOME,
        },
        {
            title: c('Sidebar').t`Dashboard`,
            icon: <ChartBarOutline />,
            to: AppRoutes.DASHBOARD,
            active: pathname.startsWith(AppRoutes.DASHBOARD),
            target: DataIntercomTarget.CTA_DASHBOARD,
        },
        {
            title: c('Sidebar').t`My needs`,
            icon: <MemoOutline />,
            to: AppRoutes.NEEDS,
            active: pathname.startsWith(AppRoutes.NEEDS),
            target: DataIntercomTarget.CTA_NEEDS,
        },
        {
            // Opens on the first tab: /activity only redirects.
            title: c('Sidebar').t`My activity`,
            icon: <LayersOutline />,
            to: AppRoutes.SERVICES,
            active: pathname.startsWith(AppRoutes.ACTIVITY),
            target: DataIntercomTarget.CTA_ACTIVITY,
        },
        {
            title: c('Sidebar').t`Partners`,
            icon: <PeopleOutline />,
            to: AppRoutes.PARTNERS,
            active: pathname.startsWith(AppRoutes.PARTNERS),
            target: DataIntercomTarget.CTA_PARTNERS,
        },
        {
            title: c('Sidebar').t`Purchase orders`,
            icon: <FolderOutline />,
            to: AppRoutes.PURCHASE_ORDERS,
            active: pathname.startsWith(AppRoutes.PURCHASE_ORDERS),
            target: DataIntercomTarget.CTA_PURCHASE_ORDERS,
        },
        {
            title: c('Sidebar').t`Invoices`,
            icon: <PageOutline />,
            to: AppRoutes.INVOICES,
            active: pathname.startsWith(AppRoutes.INVOICES),
            target: DataIntercomTarget.CTA_INVOICES,
        },
    ];

    /**
     * **Attention, trois états, et l'échec rend TOUT plutôt que rien.**
     *
     * - pendant le chargement, seule « Accueil » : afficher les sept puis en retirer
     *   quatre promettrait des pages avant de se dédire ;
     * - la requête en échec **ne raccourcit pas le menu**. On ne sait pas ce qui est
     *   ouvert, et une absence se lirait « vous n'y avez pas droit » alors qu'elle
     *   dit « nous n'avons pas pu demander ». Les pages, elles, refusent
     *   correctement : c'est le seul endroit où la vérité est tenue ;
     * - la liste vide est une **réponse juste**, pas un incident : aucun module
     *   ouvert sur aucun compte. Elle se dit, et la phrase est en dessous.
     */
    const visible = entries.filter(({ to }) => {
        const code = MODULE_OF[to];
        if (code === null) {
            return true;
        }
        if (error) {
            return true;
        }
        return openModules.has(code);
    });

    return (
        <AppSidebar
            variant="portal"
            /* HORS du contenu qui defile : `sidebar-content` est en
               `overflow: auto`, donc il coupait le menu du compte des qu'il
               depassait la largeur du rail. Voir la prop `footer`. */
            footer={
                <>
                    {/* Attention, L'ADRESSE N'EST PLUS RENDUE, et la maquette la porte encore :
                    `side-account-info` y affiche « robert.duris@yopmail.com » sous le nom.
                    Décision de Mohamed le 2026-08-26. Le nom identifie déjà la personne
                    connectée, et une adresse de connexion écrite en permanence dans un
                    coin de chaque écran est la seule donnée personnelle que le portail
                    affichait sans qu'on la lui demande. Elle reste lisible sur « Profil »,
                    à un clic d'ici.

                    `email` continue de servir de REPLI au nom : sans prénom ni nom, mieux
                    vaut une adresse qu'un « Mon compte » qui ne désigne personne. */}
                    <SidebarAccount name={displayName}>
                        {/* `?from` nomme l'origine, et c'est ce qui rend juste le lien de
                        retour d'`account` (PCG-304) : sans lui, cette page ne peut pas
                        savoir d'ou quelqu'un vient. */}
                        {import.meta.env.PUBLIC_ACCOUNT_URL && (
                            <a href={`${import.meta.env.PUBLIC_ACCOUNT_URL}/account?from=client-hub`}>
                                <UserCircleOutline />
                                {c('Sidebar').t`My account`}
                            </a>
                        )}
                        <a href="#settings" aria-disabled="true" onClick={(event) => event.preventDefault()}>
                            <SettingsOutline />
                            {c('Sidebar').t`Settings`}
                            <span className="sidebar-account__soon">{c('Sidebar soon').t`Soon`}</span>
                        </a>
                        <a href="#notifications" aria-disabled="true" onClick={(event) => event.preventDefault()}>
                            <BellOutline />
                            {c('Sidebar').t`Notifications`}
                            <span className="sidebar-account__soon">{c('Sidebar soon').t`Soon`}</span>
                        </a>
                        <hr />
                        <a
                            href="#logout"
                            className="sidebar-account__logout"
                            onClick={(event) => {
                                event.preventDefault();
                                void logout();
                            }}
                        >
                            <LogoutOutline />
                            {c('Sidebar').t`Logout`}
                        </a>
                    </SidebarAccount>
                </>
            }
        >
            <SidebarNav>
                <SidebarList>
                    {visible.map(({ title, icon, to, active, target }) => (
                        <SidebarListItem
                            key={to}
                            active={active}
                            title={title}
                            icon={icon}
                            activeIcon={icon}
                            to={to}
                            tooltipProps={{ 'data-intercom-target': target }}
                        />
                    ))}

                    {/* Un menu réduit à « Accueil » sans un mot se lit comme un portail
                        cassé, et c'est l'état ordinaire de la plupart des comptes.

                        Attention, la phrase parle « de vos comptes » et envoie demander
                        l'ouverture d'un module : elle suppose donc au moins un compte
                        rattaché. Pour une personne qui n'en a aucun (PCG-118) elle est
                        fausse et désigne le mauvais geste : c'est un rattachement qu'il
                        lui manque, pas un module. L'accueil le dit à sa place, et
                        seulement une fois. On se tait ici uniquement quand on SAIT
                        qu'il n'y a aucun rattachement : en cours de chargement ou en
                        échec, la phrase sur les modules reste la bonne. */}
                    {!error && visible.length === 1 && attachment.status !== 'unattached' && (
                        <li className="sidebar-list__note" role="status">
                            {loading
                                ? c('Sidebar').t`Loading your modules…`
                                : c('Sidebar')
                                      .t`No module is open on your accounts yet. Ask your Freelance.com contact to open the ones you need.`}
                        </li>
                    )}
                </SidebarList>
            </SidebarNav>
        </AppSidebar>
    );
};

export default Sidebar;
